<template>
  <div>
    <Head :title="`Editar: ${form.name}`" />

    <!-- Header & Breadcrumbs -->
    <div class="mb-8">
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-4">
        <button class="hover:text-teal-600 transition-colors" title="Volver atrás" @click="back()">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/spents" class="hover:text-teal-600 transition-colors">Gastos</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">Editar Gasto</span>
      </div>

      <h1 class="text-3xl font-bold text-gray-900">Editar Detalle de Gasto</h1>
    </div>

    <!-- Form -->
    <div class="max-w-3xl">
      <form @submit.prevent="update">
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div class="p-8">
            <div class="mb-6 pb-2 border-b border-gray-50">
              <h2 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i class="fa fa-edit text-teal-600" />
                Información del Gasto
              </h2>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div class="md:col-span-2">
                <text-input
                  v-model="form.name"
                  :error="form.errors.name"
                  label="Concepto / Nombre *"
                  placeholder="Ej: Pago de electricidad Marzo"
                  class="teal-focus"
                />
              </div>

              <text-input
                v-model="form.date"
                :error="form.errors.date"
                type="date"
                label="Fecha del Gasto *"
                class="teal-focus"
              />

              <!-- Currency and Amount Logic -->
              <div class="flex flex-col gap-1">
                <label class="form-label font-semibold text-gray-700 mb-1">Moneda *</label>
                <div class="flex bg-gray-100 p-1 rounded-xl w-fit">
                  <button 
                    type="button"
                    :class="form.currency === 'USD' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-500'"
                    class="px-4 py-2 rounded-lg text-sm font-bold transition-all"
                    @click="setCurrency('USD')"
                  >
                    USD
                  </button>
                  <button 
                    type="button"
                    :class="form.currency === 'BS' ? 'bg-white shadow-sm text-teal-600' : 'text-gray-500'"
                    class="px-4 py-2 rounded-lg text-sm font-bold transition-all"
                    @click="setCurrency('BS')"
                  >
                    BS
                  </button>
                </div>
              </div>

              <div v-if="form.currency === 'BS'" class="md:col-span-1">
                <text-input
                  v-model="bsAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  label="Monto en BS *"
                  placeholder="0.00"
                  class="teal-focus"
                />
                <p class="text-xs text-gray-500 mt-1 italic">
                  Tasa: {{ form.exchange_rate }} BS/USD
                </p>
              </div>

              <div :class="form.currency === 'BS' ? 'md:col-span-1' : 'md:col-span-1'">
                <text-input
                  v-model="form.amount"
                  :error="form.errors.amount"
                  type="number"
                  min="0"
                  step="0.01"
                  :label="form.currency === 'BS' ? 'Monto en USD (Calculado)' : 'Monto (USD) *'"
                  placeholder="0.00"
                  class="teal-focus"
                  :readonly="form.currency === 'BS'"
                />
              </div>

              <select-input
                v-model="form.spent_category_id"
                :error="form.errors.spent_category_id"
                label="Categoría *"
                class="teal-focus"
              >
                <option value="">Seleccionar categoría</option>
                <option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select-input>

              <select-input
                v-model="form.method"
                :error="form.errors.method"
                label="Método de pago *"
                class="teal-focus"
              >
                <option value="">Seleccionar método</option>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="pago-movil">Pago Móvil</option>
                <option value="zelle">Zelle</option>
              </select-input>
            </div>
          </div>

          <div class="px-8 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-4">
            <button 
              class="text-sm font-bold text-red-500 hover:text-red-700 transition-colors"
              type="button"
              @click="destroy"
            >
              <i class="fa fa-trash-alt mr-1" />
              Eliminar Gasto
            </button>
            
            <div class="flex items-center gap-4">
              <Link href="/spents" class="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors">
                Cancelar
              </Link>
              <loading-button 
                :loading="form.processing" 
                class="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-black rounded-xl transition-all shadow-lg shadow-teal-100 flex items-center gap-2"
                type="submit"
              >
                <i class="fa fa-save" />
                Actualizar Gasto
              </loading-button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import Swal from 'sweetalert2'

export default {
  name: 'SpentEdit',

  components: {
    Head,
    Link,
    LoadingButton,
    TextInput,
    SelectInput,
  },

  layout: Layout,

  props: {
    spent: Object,
    categories: Object,
    currentExchangeRate: {
      type: [Number, String],
      default: 0,
    },
  },

  remember: 'form',

  data() {
    return {
      bsAmount: this.spent.currency === 'BS' ? (this.spent.amount * this.spent.exchange_rate).toFixed(2) : null,
      form: this.$inertia.form({
        name: this.spent?.name,
        amount: this.spent.amount,
        date: this.spent?.date,
        method: this.spent?.method,
        spent_category_id: this.spent.spent_category_id,
        currency: this.spent.currency || 'USD',
        exchange_rate: this.spent.exchange_rate || this.currentExchangeRate,
      }),
    }
  },

  watch: {
    bsAmount: {
      handler: 'calculateFromBs',
    },
  },

  methods: {
    setCurrency(currency) {
      this.form.currency = currency
      if (currency === 'USD') {
        this.bsAmount = null
        this.form.exchange_rate = null
      } else {
        this.form.exchange_rate = this.spent.exchange_rate || this.currentExchangeRate
        this.calculateFromBs()
      }
    },

    calculateFromBs() {
      if (this.bsAmount && this.form.exchange_rate > 0) {
        this.form.amount = (this.bsAmount / this.form.exchange_rate).toFixed(2)
      } else {
        this.form.amount = 0
      }
    },

    back() {
      this.$inertia.visit('/spents')
    },

    update() {
      this.form.put(`/spents/${this.spent.id}`, {
        onSuccess: () => {
          Swal.fire({
            title: '¡Actualizado!',
            text: 'El gasto ha sido actualizado correctamente.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          })
        },
      })
    },

    async destroy() {
      const result = await Swal.fire({
        title: '¿Eliminar gasto?',
        text: 'Esta acción no se puede deshacer. ¿Estás seguro?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#9ca3af',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      })

      if (result.isConfirmed) {
        this.$inertia.delete(`/spents/${this.spent.id}`)
      }
    },
  },
}
</script>

<style scoped>
:deep(.teal-focus input:focus),
:deep(.teal-focus select:focus) {
  --tw-ring-color: #0d9488;
  border-color: #0d9488;
}
</style>